import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { PageContainer } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import TransferRewardsDrawer from "../../components/admin/rewards/TransferRewardsDrawer";
import RewardsHistoryTable from "../../components/admin/rewards/RewardsHistoryTable";
import RewardsBalance from "../../components/admin/rewards/RewardsBalance";
import RewardsTransactionTable from "../../components/admin/rewards/RewardsTransactionTable";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import { useQuery } from "@apollo/client";
import {
  GET_LIST_BANK_ACCOUNTS,
  GET_REWARDS_BALANCE,
} from "../../graphql/company";
import ConnectedFundingAccountDrawer from "../../components/admin/bank/ConnectedFundingAccountDrawer";
import AccountType from "enums/AccountType";
import AccountStatus from "enums/AccountStatus";

const Rewards: React.FC = () => {
  const { t } = useTranslation();

  const [openTransactionReviewDrawer, setOpenTransactionReviewDrawer] =
    useState<boolean>(false);
  const [transactionId, setTransactionId] = useState<string>("");
  const [refreshDataIndex, setRefreshDataIndex] = useState<number>(0);
  const [openTransferRewards, setOpenTransferRewards] =
    useState<boolean>(false);

  const handleOpenTransferRewards = () => {
    setOpenTransferRewards(true);
  };

  const handleOpenConnectedFundsDrawer = () => {
    setOpenDrawerConnectedFunding(true);
  };

  const handleCloseConnectedFundsDrawer = () => {
    setOpenDrawerConnectedFunding(false);
  };

  const [openDrawerConnectedFunding, setOpenDrawerConnectedFunding] =
    useState<boolean>(false);

  const {
    data: rewardsBalance,
    loading: loadingRewardsBalance,
    refetch: refetchBalance,
  } = useQuery(GET_REWARDS_BALANCE, {
    fetchPolicy: "network-only",
  });

  const {
    data: bankAccountsData,
    refetch: refetchBankAccounts,
    loading: loadingAccounts,
  } = useQuery(GET_LIST_BANK_ACCOUNTS, {
    variables: {
      status: [AccountStatus.ACTIVE, AccountStatus.MICRODEPOSITS_PENDING],
      types: [AccountType.BANK, AccountType.REWARDS_BANK],
    },
    fetchPolicy: "network-only",
  });

  const rewardBankAccountData = bankAccountsData?.getListBankAccounts || [];

  const availableRewardsBalance = useMemo(
    () => ({
      amount: rewardsBalance?.getRewardsBalance?.available?.amount || 0,
      currency: rewardsBalance?.getRewardsBalance?.available?.currency || "USD",
    }),
    [rewardsBalance],
  );

  const pendingRewardsBalance = useMemo(
    () => ({
      amount: rewardsBalance?.getRewardsBalance?.pending?.amount || 0,
      currency: rewardsBalance?.getRewardsBalance?.pending?.currency || "USD",
    }),
    [rewardsBalance],
  );

  const withheldRewardsBalance = useMemo(
    () => ({
      amount: rewardsBalance?.getRewardsBalance?.withheld?.amount || 0,
      currency: rewardsBalance?.getRewardsBalance?.withheld?.currency || "USD",
    }),
    [rewardsBalance],
  );

  const handleCloseTransferRewards = () => {
    setOpenTransferRewards(false);
  };

  const handleRefreshAllData = () => {
    setRefreshDataIndex(refreshDataIndex + 1);
    refetchBalance();
  };

  const handleOpenReviewDrawer = (transactionId: string) => {
    setOpenTransferRewards(false);
    setTransactionId(transactionId);
    setOpenTransactionReviewDrawer(true);
  };

  const handleCloseReviewDrawer = () => {
    setOpenTransactionReviewDrawer(false);
    setTransactionId("");
  };

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        boxShadow: "var(--shadow-lg)",
      }}
    >
      <PageContainer
        title={t("Rewards")}
        wrapped={false}
        primaryAction={
          <Button onClick={handleOpenTransferRewards}>
            {t("Transfer Rewards")}
          </Button>
        }
        secondaryAction={
          <Button
            variant="primary-outlined"
            onClick={handleOpenConnectedFundsDrawer}
          >
            {t("Rewards Accounts")}
          </Button>
        }
      >
        <Stack gap="var(--space-5)" sx={{ paddingBottom: "var(--space-7)" }}>
          <RewardsBalance
            availableRewardsBalance={availableRewardsBalance}
            pendingRewardsBalance={pendingRewardsBalance}
            withheldRewardsBalance={withheldRewardsBalance}
            loadingRewardsBalance={loadingRewardsBalance}
          />
          <RewardsHistoryTable refreshDataIndex={refreshDataIndex} />
          <RewardsTransactionTable
            refreshDataIndex={refreshDataIndex}
            handleOpenReviewDrawer={handleOpenReviewDrawer}
          />
        </Stack>

        <TransferRewardsDrawer
          open={openTransferRewards}
          handleCloseDrawer={handleCloseTransferRewards}
          availableRewardsBalance={availableRewardsBalance}
          handleRefreshAllData={handleRefreshAllData}
        />

        <TransactionDetailsDrawer
          open={openTransactionReviewDrawer}
          handleCloseDrawer={handleCloseReviewDrawer}
          transactionId={transactionId}
          handleRefreshTransactions={handleRefreshAllData}
        />

        <ConnectedFundingAccountDrawer
          open={openDrawerConnectedFunding}
          handleCloseDrawer={handleCloseConnectedFundsDrawer}
          bankAccountsData={rewardBankAccountData}
          refetchAccounts={refetchBankAccounts}
          optionalTitle={t("Connected Rewards Account")}
          accountType={AccountType.BANK}
          loadingBankAccountData={loadingAccounts}
          rewards
        />
      </PageContainer>
    </Box>
  );
};

export default Rewards;
